import { RootDispatch, RootState } from '../store';
import { Pages, VisitStatusUpdateEvent, VWRCallEvent } from '../types';
import { ShowMessengerParams } from '../types/API/VirtualWaitingRoom';
import {
  get as getLocalStorageItem,
  set as setLocalStorageItem,
  RESERVED_KEYS,
} from '../services/SessionStore';
import { getConnector } from '../services/PatientFacingClient';
import { trackVirtualWaitingRoomInformProviderModalButtonPressed } from '../services/Tracker';
import { ClientConnector } from '../services/sdkConnector/ClientConnector';

export type VirtualWaitingRoomState = {
  incomingCall?: VWRCallEvent;
  shouldModalShowIfIncomingCallExists: boolean;
  isSessionEnded: boolean;
};

const initialState: VirtualWaitingRoomState = {
  incomingCall: undefined,
  shouldModalShowIfIncomingCallExists: true,
  isSessionEnded: false,
};

export function hasCallModalBeenHiddenForCallId(callId: string): boolean {
  try {
    return callId === getLocalStorageItem(RESERVED_KEYS.HIDDEN_CALL).value;
  } catch (error) {
    return false;
  }
}

let timeoutRef: number | undefined;

export const virtualWaitingRoom = {
  effects: (dispatch: RootDispatch) => ({
    joinCall(_: undefined, rootState: RootState) {
      if (!rootState.virtualWaitingRoom.incomingCall) return;

      trackVirtualWaitingRoomInformProviderModalButtonPressed();
      dispatch.virtualWaitingRoom.hideCallModal(undefined);

      const { origin, pathname, search } = window.location;
      const urlParams = new URLSearchParams(search);
      urlParams.delete('r');
      urlParams.delete('v');
      urlParams.set('c', rootState.virtualWaitingRoom.incomingCall.vwr_visitor_access_token);
      urlParams.set('source', 'vwr');
      window.open(`${origin + pathname}?${urlParams.toString()}`);
    },

    handleVWRCallEvent(event: VWRCallEvent) {
      if (event.status !== 'invite') {
        clearTimeout(timeoutRef);
        dispatch.virtualWaitingRoom.clearCall();
      } else {
        const shouldShowModal = !hasCallModalBeenHiddenForCallId(event.twilio_room_name);

        //eslint-disable-next-line scanjs-rules/call_setTimeout
        timeoutRef = window.setTimeout(() => {
          dispatch.virtualWaitingRoom.clearCall();
        }, event.ttl * 1000);
        dispatch.virtualWaitingRoom.updateIncomingCall(event);
        dispatch.virtualWaitingRoom.updateIncomingCallVisibility(shouldShowModal);
      }
    },

    clearCall() {
      dispatch.virtualWaitingRoom.updateIncomingCall(undefined);
      dispatch.virtualWaitingRoom.updateIncomingCallVisibility(true);
    },

    hideCallModal(_: undefined, rootState: RootState) {
      try {
        // eslint-disable-next-line scanjs-rules/identifier_localStorage, scanjs-rules/property_localStorage
        setLocalStorageItem(RESERVED_KEYS.HIDDEN_CALL, {
          value: rootState.virtualWaitingRoom.incomingCall?.twilio_room_name,
        });
      } catch (error) {
        console.error('Unable to persist call id to localstorage', error);
      } finally {
        dispatch.virtualWaitingRoom.updateIncomingCallVisibility(false);
      }
    },

    async showMessenger({
      accountId,
      accountKey,
      accountSecret,
      conversationId,
      groupId,
      sessionToken,
      organizationId,
    }: ShowMessengerParams) {
      dispatch.currentPage.update(Pages.Loading);
      // https://tigertext.atlassian.net/browse/APP-101
      const connector = getConnector() as ClientConnector;

      try {
        setLocalStorageItem(RESERVED_KEYS.VWR_SESSION, {
          session_token: sessionToken,
          account_token: accountId,
        });

        await connector.client.virtualWaitingRoom.visitorLogin({
          groupId,
          accountKey,
          accountSecret,
          organizationId,
        });

        connector.store.dispatch.organization.setOrganization({
          id: organizationId,
          allowAttachment: true,
          allowPhoto: true,
        });

        const conversation = await connector.client.conversations.find(conversationId, {
          ensureEntities: {
            counterParty: 'full',
            escalationExecution: false,
            groupMembers: 'full',
            messageCounterParties: 'full',
            organization: false,
            touchOnRelatedEntityLoad: false,
          },
          includeMutes: false,
          groupId: groupId,
          organizationId: organizationId,
          isVwrFeatureEnabled: true,
        });
        connector.store.dispatch.conversation.setConversation(conversation);
        await connector.selectConversation(conversation, { isVwrFeatureEnabled: true });

        dispatch.conversation.markConversationAsRead(true);

        dispatch.currentPage.update(Pages.Messenger);
      } catch (error) {
        // TODO: UPDATE FOR RE JOIN
        console.error(error);
        // if (error.status === 429) {
        //   dispatch.currentPage.update(Pages.TooManyRequests);
        //   dispatch.patient.setPatient({ errors: MAX_ATTEMPTS });
        //   return;
        // }
        //
        // connector.store.dispatch.patient.setPatient({
        //   errors: rootState.patient.errors ? rootState.patient.errors + 1 : 1,
        // });
        // await dispatch.currentPage.showInputAccessCode();
      }
    },
    handleVwrVisitChange(visitChangeEvent: VisitStatusUpdateEvent | undefined) {
      if (visitChangeEvent?.status === 'COMPLETED') {
        this.showSessionEnded(true);
      }
    },
    showSessionEnded(isSessionEnded: boolean) {
      dispatch.virtualWaitingRoom.updateSessionEnded(isSessionEnded);
    },
  }),
  reducers: {
    updateSessionEnded: (state: VirtualWaitingRoomState, isSessionEnded: boolean) => ({
      ...state,
      isSessionEnded,
    }),
    updateIncomingCall: (
      state: VirtualWaitingRoomState,
      incomingCall?: VWRCallEvent
    ): VirtualWaitingRoomState => ({
      ...state,
      incomingCall,
    }),
    updateIncomingCallVisibility: (
      state: VirtualWaitingRoomState,
      shouldModalShowIfIncomingCallExists: boolean
    ): VirtualWaitingRoomState => ({
      ...state,
      shouldModalShowIfIncomingCallExists,
    }),
  },
  state: initialState,
};
